import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
} from '@tanstack/react-table';
import Table from 'components/Table';
import { plainTextToPhoneNumber } from 'shared/util/util';
import { useQuery } from 'react-query';
import format from 'date-fns/format';
import { getInvitedUsers } from 'api/users/api';
import {
  TokenUserPermissions,
  TokenUserRoles,
  USERS_LIMIT,
} from '../../api/users/schemas/Users';
import { isAfterDate } from '../../components/Table/filters';
import {
  InvitationItem,
  InvitationMetaDataResponse,
  InvitationResponse,
} from '../../api/users/schemas/Invitations';
import { INVITE_TABLE_QK } from './useInviteTableRefetch';
import { usePermissions } from 'hooks/usePermissions';

import RoleTag from 'screens/RolesPermissions/CustomRoles/RoleTag';
import { getAllRoles } from 'screens/RolesPermissions/utils';
import { AvatarNameCell } from './AvatarNameCell';
import { InvitationActions } from './InvitationActions';
import { usePaginatedTable } from 'hooks/usePaginatedTable';

interface UserManagementTableProps {
  columnFilterState: ColumnFiltersState;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  globalFilterState: string | undefined;
  onGlobalFilterChange: OnChangeFn<string | undefined>;
  onRolesOptionsChange: (roles: string[]) => void;
}

const InviteManagementTable = ({
  columnFilterState,
  onColumnFiltersChange,
  globalFilterState,
  onGlobalFilterChange,
  onRolesOptionsChange,
}: UserManagementTableProps) => {
  const { hasPermission } = usePermissions({
    permission: TokenUserPermissions.MANAGE_GROUPS,
  });
  const { data, isFetchingData } = usePaginatedTable<
    InvitationItem,
    InvitationMetaDataResponse
  >(
    () =>
      getInvitedUsers({
        page_size: USERS_LIMIT,
      }),
    {},
    [INVITE_TABLE_QK],
    {
      refetchOnMount: true,
      onSuccess: (data: InvitationResponse) =>
        onRolesOptionsChange(data.metadata.roles ?? []),
    },
  );

  const { data: roles, isFetching: isFetchingRoles } = useQuery(
    ['user-form-all-roles'],
    () => getAllRoles(),
    {
      refetchOnWindowFocus: false,
    },
  );

  const InvitationManagementTableDef: ColumnDef<InvitationItem>[] = [
    {
      id: 'name',
      header: 'User',
      accessorKey: 'name',
      accessorFn: (row) => `${row.first_name} ${row.last_name}`,
      cell: (row) => (
        <AvatarNameCell
          name={`${row?.row?.original?.first_name ?? ''} ${
            row?.row?.original?.last_name ?? ''
          }`}
        />
      ),
    },
    {
      id: 'role',
      header: 'Role',
      accessorKey: 'role',
      cell: (row) => {
        const role = roles?.find((r) => r.name === row?.row?.original?.role);
        return (
          <RoleTag
            name={role?.name ?? TokenUserRoles.VIEWER}
            color={role?.color ?? ''}
          />
        );
      },
      enableGlobalFilter: false,
    },
    {
      id: 'phone_number',
      header: 'Phone',
      enableSorting: false,
      cell: (row) =>
        row?.row?.original?.phone_number
          ? plainTextToPhoneNumber(row?.row?.original?.phone_number)
          : ' -- ',
      accessorKey: 'phone_number',
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
    },
    {
      id: 'created_at',
      header: 'Date created',
      cell: (row) =>
        row?.row?.original?.created_at
          ? format(new Date(row?.row?.original?.created_at), 'MM/dd/yyyy')
          : ' -- ',
      accessorKey: 'created_at',
      filterFn: isAfterDate,
      enableGlobalFilter: false,
    },
    {
      header: 'Actions',
      enableSorting: false,
      cell: (cell) => <InvitationActions row={cell.row} />,
      enableGlobalFilter: false,
    },
  ];

  const formattedData =
    hasPermission && !isFetchingData
      ? data
      : data.filter((item) => !item.email.endsWith('@granitenet.com'));

  return (
    <Table
      data={formattedData ?? []}
      columns={InvitationManagementTableDef}
      title="Users"
      columnFiltersState={columnFilterState}
      onColumnFiltersChange={onColumnFiltersChange}
      isLoading={isFetchingData || isFetchingRoles}
      globalFilterState={globalFilterState}
      onGlobalFilterChange={onGlobalFilterChange}
    />
  );
};

export default InviteManagementTable;
